// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {Box, Button, EllipsisOverflow, MultiSelect, Stack} from '@deere/isg.component-library';
import Panel from 'Ui/components/panel/panel';
import NoDataMessage from 'Ui/components/panel/no-data-message';
import {useDeepMemo} from 'Utils/react-utils';
import {formatDateOrDefault, formatEquipmentCutHeight} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import DataGridWrapper from '../../common/data-table/data-grid-wrapper';
import {dateCompare} from 'Src/ui-core/models/maintenance';
import {EquipmentIcon, IconWrench} from '@deere/icons';
import {connect} from 'react-redux';

const ICON_SIZE = 24;
const CELL_GAP = 8;
const CELL_HORIZONTAL_PADDING = 20;

const CUT_HEIGHT_FIELD = 'rawCutHeight';

// Custom comparator to sort cut height values, with nulls at the bottom in descending order and tie-breaking by equipment name in ascending order.
function cutHeightSortComparator(v1, v2, param1, param2) {
    if (v1 !== v2) {
        return (v2 ?? Infinity) - (v1 ?? Infinity);
    }

    const direction = param1.api.getSortModel()
        .find((item) => item.field === CUT_HEIGHT_FIELD)?.sort ?? 'asc';
    const secondaryMultiplier = direction === 'desc' ? -1 : 1;

    const nameA = param1.api.getRow(param1.id).equipmentName ?? '';
    const nameB = param2.api.getRow(param2.id).equipmentName ?? '';

    return secondaryMultiplier * nameA.localeCompare(nameB);
}

function getDataGridColumns(translations) {
    return [
        {
            headerName: translations.MACHINE,
            field: 'equipmentName',
            flex: 1,
            renderCell: (params) => {
                const {
                    icon, equipmentName
                } = params.row;

                const iconSpace = icon ? ICON_SIZE + CELL_GAP : 0;
                const textWidth = params.colDef.computedWidth - CELL_HORIZONTAL_PADDING - iconSpace;

                return (
                    <Stack
                        alignItems='center'
                        direction='row'
                        gap={`${CELL_GAP}px`}
                    >
                        {icon && <EquipmentIcon
                            iconName={icon.name}
                            primary={icon.iconStyle.primaryColor}
                            secondary={icon.iconStyle.secondaryColor}
                            size={ICON_SIZE}
                        />}
                        <EllipsisOverflow
                            slotProps={{
                                typography: {
                                    component: 'p'
                                }
                            }}
                            style={{
                                width: textWidth
                            }}
                            tooltipPlacement='right'
                        >
                            {equipmentName}
                        </EllipsisOverflow>
                    </Stack>
                );
            }
        },
        {
            headerName: translations.CUT_HEIGHT,
            field: CUT_HEIGHT_FIELD,
            flex: 1,
            sortComparator: cutHeightSortComparator,
            renderCell: (params) => params.row.formattedCutHeight
        }, {
            headerName: translations.MODIFIED_CAPITALIZED,
            field: 'cutHeightLastModified',
            flex: 1,
            sortComparator: dateCompare,
            renderCell: (params) => {
                const {cutHeightLastModified} = params.row;

                return formatDateOrDefault(cutHeightLastModified);
            }
        }
    ];
}

function createMultiSelectItems(equipment) {
    return Array.from(new Map(
        equipment.map((eq) => [eq.modelId, {
            id: eq.modelId,
            title: eq.modelName
        }])
    ).values());
}

function CutHeight(props) {
    const {
        tile,
        translations,
        unitOfMeasure
    } = props;

    const [selectedModels, setSelectedModels] = React.useState([]);

    const equipment = (tile?.equipment?.filter((eq) => eq.isMowingEquipment) || []).map((eq) => ({
        ...eq,
        ...formatEquipmentCutHeight(eq.cutHeight, unitOfMeasure, translations)
    }));

    const {
        columns, items
    } = useDeepMemo(() => ({
        columns: getDataGridColumns(translations),
        items: createMultiSelectItems(equipment)
    }), [equipment, translations]);

    const filteredEquipment = useDeepMemo(() =>
        selectedModels.length > 0 ? equipment.filter((eq) => selectedModels.includes(eq.modelId)) : equipment
    , [selectedModels, equipment]);

    return (
        <Panel
            title={translations.ONLINK_MACHINE_CUT_HEIGHTS}
            translations={translations}
        >
            <Box
                display='flex'
                flexDirection='column'
                gap='16px'
            >
                <Box width='50%'>
                    <MultiSelect
                        items={items}
                        labels={{
                            allSelect: translations.ALL_MACHINES,
                            selectAllItems: `${translations.SELECT_ALL} ${translations.MODELS}`,
                            emptyList: translations.NO_EQUIPMENT_FOUND,
                            placeholder: translations.ALL_MACHINES,
                            multiSelect: translations.MODELS
                        }}
                        onChange={setSelectedModels}
                        selectedIds={selectedModels}
                    />
                </Box>
                {
                    filteredEquipment.length > 0 ? (
                        <DataGridWrapper
                            columns={columns}
                            enableDrillIn
                            getRowId={(row) => row.equipmentId}
                            initialState={{
                                sorting: {
                                    sortModel: [{
                                        field: 'rawCutHeight',
                                        sort: 'desc'
                                    }]
                                }
                            }}
                            maxVisibleRows={5}
                            noDataAvailableMessage={translations.NO_EQUIPMENT_FOUND}
                            onDrillIn={(params) => {
                                const {equipmentId} = params.row;

                                window.open(`${window.props.maintenanceManagerLink}/machines/${equipmentId}/maintenanceLog`, '_blank');
                            }}
                            rows={filteredEquipment}
                            slotProps={{
                                root: {
                                    disableColumnMenu: true
                                }
                            }}
                            translations={translations}
                        />
                    ) : <NoDataMessage translations={translations}/>
                }
                <Box
                    display='flex'
                    justifyContent='flex-end'
                >
                    <Button
                        onClick={() => window.open(`${window.props.maintenanceManagerLink}/allEquipment`, '_blank')}
                        startIcon={<IconWrench/>}
                        variant='tertiary'
                    >
                        {translations.ONLINK_MAINTENANCE}
                    </Button>
                </Box>
            </Box>

        </Panel>
    );
}

CutHeight.propTypes = {
    tile: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.object)
    ]),
    translations: PropTypes.translations,
    unitOfMeasure: PropTypes.string
};

export function mapStateToProps(state) {
    return {
        unitOfMeasure: state.membership.unitOfMeasure
    };
}

export default connect(mapStateToProps)(CutHeight);
